// ============================================================
// Full path:
//   D:\Thyroid Consultation Software\ThyroConsult Frontend\thyroconsult-frontend\src\components\ContactUs.js
//
// "Contact Us" (28 Sep 2026) — one modal, two modes:
//   mode="public"  Landing page footer: enter email → verify with a code
//                  → message (up to 500 words) → Submit.
//   mode="user"    Patient / doctor dashboard (sidebar, just above Sign
//                  out): message (up to 500 words) → Submit. The account's
//                  own email is used, so no verification step.
// Server: controllers/contactController.js. Messages reach the admin under
// Admin → Contact messages.
//
// 16 Oct 2026: "Send verification code" waits for the background security
// check instead of failing when pressed straight away.
//
// 9 Oct 2026: the code box accepts up to 8 digits (was fixed at 6), so it
// works with 6- or 7-digit codes while OTP length moves to 7. Verify still
// enables from 4 digits; the server decides whether the code is right.
// ============================================================

import React, { useEffect, useRef, useState } from 'react';
import Turnstile from './Turnstile';
import { createPortal } from 'react-dom';
import { contactAPI } from '../api';
// English / Hindi (13 Oct 2026, Hindi version)
import { useI18n } from '../i18n';

export const CONTACT_MAX_WORDS = 500;
const CODE_MAX_DIGITS = 8;
const countWords = (t) => (String(t || '').trim().match(/\S+/g) || []).length;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const errText = (err, fallback) => err?.response?.data?.error || err?.message || fallback;

export function ContactUsModal({ mode = 'user', onClose }) {
  const { t } = useI18n();
  const [email, setEmail] = useState('');
  const [codeSent, setCodeSent] = useState(false);
  const [code, setCode] = useState('');
  const [proof, setProof] = useState('');
  const [wait, setWait] = useState(0);
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [done, setDone] = useState('');

  const words = countWords(message);
  const tooLong = words > CONTACT_MAX_WORDS;
  const verified = mode === 'user' || !!proof;

  useEffect(() => { if (!wait) return undefined; const t = setTimeout(() => setWait(w => w - 1), 1000); return () => clearTimeout(t); }, [wait]);
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape' && !busy) onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [busy, onClose]);

  // "I'm not a robot" check for the public form's code (1 Oct 2026)
  // The check runs unseen in the background and its token arrives a moment
  // after the window opens. Pressing "Send verification code" before that
  // used to send no token, and the server answered "Please complete the
  // security check" with no box on screen (16 Oct 2026). Send now waits
  // for the token (up to 15 s; the button shows "Sending…"). If Cloudflare
  // needs a tick, the person ticks it in the box.
  // The box is always shown (appearance="always", 16 Oct 2026) so the
  // person can see the check, as on the registration page.
  const tsTokenRef = useRef('');
  const tsErrorRef = useRef(false);
  const ts = useRef(null);
  const onTsToken = (tok) => { tsTokenRef.current = tok || ''; if (tok) tsErrorRef.current = false; };
  const onTsError = () => { tsErrorRef.current = true; };
  const waitForTsToken = async (ms = 15000) => {
    const end = Date.now() + ms;
    while (!tsTokenRef.current && !tsErrorRef.current && Date.now() < end) {
      await new Promise(r => setTimeout(r, 250));
    }
    return tsTokenRef.current;
  };
  const sendCode = async () => {
    if (!EMAIL_RE.test(email.trim())) { setError(t('Please enter a valid email address')); return; }
    setBusy(true); setError(''); setInfo('');
    try {
      const token = tsTokenRef.current || await waitForTsToken();
      await contactAPI.sendCode(email.trim().toLowerCase(), token || undefined);
      setCode(''); // new code → clear the old one (1 Oct 2026)
      setCodeSent(true); setWait(30);
      setInfo(t('A code has been sent to your email. It is valid for 10 minutes.'));
    } catch (err) { setError(errText(err, t('Could not send the code.'))); }
    // a used check token is never sent again — the check gives a new one (9 Oct 2026)
    finally { setBusy(false); tsTokenRef.current = ''; tsErrorRef.current = false; if (ts.current) ts.current.reset(); }
  };

  const verify = async () => {
    setBusy(true); setError(''); setInfo('');
    try {
      const r = await contactAPI.verifyCode(email.trim().toLowerCase(), code.trim());
      if (r && r.proof) { setProof(r.proof); setInfo(''); }
    } catch (err) { setError(errText(err, t('Incorrect code.'))); }
    finally { setBusy(false); }
  };

  const submit = async () => {
    if (!message.trim()) { setError(t('Please write your message')); return; }
    if (tooLong) { setError(t('Please keep your message to {n} words or fewer', { n: CONTACT_MAX_WORDS })); return; }
    setBusy(true); setError('');
    try {
      const r = mode === 'public'
        ? await contactAPI.submitPublic(email.trim().toLowerCase(), proof, message.trim())
        : await contactAPI.submit(message.trim());
      setDone(t((r && r.message) || 'Thank you — your message has been sent.'));
    } catch (err) { setError(errText(err, t('Could not send your message.'))); }
    finally { setBusy(false); }
  };

  const input = { width: '100%', padding: '9px 11px', borderRadius: 8, border: '1px solid var(--border, #cbd5e1)', fontSize: 14, fontFamily: 'inherit', boxSizing: 'border-box' };

  // Rendered into <body> through a portal: the sidebar is CSS-transformed
  // on mobile, which would otherwise trap a position:fixed overlay inside
  // it. stopPropagation keeps clicks here from reaching the sidebar's own
  // "close on click" handler (React events bubble through portals).
  return createPortal(
    <div role="dialog" aria-modal="true" aria-label={t('Contact Us')}
      onClick={(e) => { e.stopPropagation(); if (e.target === e.currentTarget && !busy) onClose(); }}
      style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div style={{ background: '#fff', borderRadius: 14, width: '100%', maxWidth: 520, maxHeight: '92vh', overflowY: 'auto', padding: '22px 22px 18px', boxShadow: '0 20px 50px rgba(0,0,0,0.2)', textAlign: 'left' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <h3 style={{ margin: 0, fontSize: 18 }}>{t('Contact Us')}</h3>
          <button type="button" onClick={onClose} disabled={busy} aria-label={t('Close')}
            style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#64748b', lineHeight: 1 }}>✕</button>
        </div>

        {done ? (
          <>
            <div style={{ padding: '12px 14px', borderRadius: 8, background: '#dcfce7', color: '#166534', fontSize: 14, marginBottom: 14 }}>{done}</div>
            <button type="button" className="btn btn-primary" onClick={onClose}>{t('Close')}</button>
          </>
        ) : (
          <>
            {mode === 'public' && (
              <div style={{ marginBottom: 14 }}>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6 }}>{t('Your email address')}</label>
                <input type="email" value={email} disabled={!!proof || busy} style={input} placeholder={t('name@example.com')}
                  onChange={e => { setEmail(e.target.value); setCodeSent(false); setCode(''); setProof(''); }} />
                {proof ? (
                  <div style={{ fontSize: 13, color: '#166534', fontWeight: 600, marginTop: 6 }}>{t('✓ Email verified')}</div>
                ) : !codeSent ? (
                  <button type="button" className="btn btn-sm btn-secondary" style={{ marginTop: 8 }} disabled={busy || !EMAIL_RE.test(email.trim())} onClick={sendCode}>
                    {busy ? t('Sending…') : t('Send verification code')}
                  </button>
                ) : (
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginTop: 8 }}>
                    <input inputMode="numeric" maxLength={CODE_MAX_DIGITS} value={code} placeholder={t('Code from email')}
                      onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, CODE_MAX_DIGITS))}
                      style={{ ...input, width: 160, letterSpacing: 2 }} />
                    <button type="button" className="btn btn-sm btn-primary" disabled={busy || code.length < 4} onClick={verify}>{busy ? t('Checking…') : t('Verify')}</button>
                    <button type="button" className="btn btn-sm btn-ghost" disabled={busy || wait > 0} onClick={sendCode}>{wait > 0 ? t('Resend in {s}s', { s: wait }) : t('Resend code')}</button>
                  </div>
                )}
                {!proof && <Turnstile ref={ts} onToken={onTsToken} onError={onTsError} action="contact_code" appearance="always" />}
                {info && <div style={{ fontSize: 12, color: 'var(--text-tertiary, #64748b)', marginTop: 6 }}>{t(info)}</div>}
              </div>
            )}

            <div style={{ opacity: verified ? 1 : 0.5 }}>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6 }}>{t('Your message')}</label>
              <textarea value={message} onChange={e => setMessage(e.target.value)} rows={8} disabled={!verified || busy}
                placeholder={verified ? t('Write your message here (up to 500 words)') : t('Verify your email first')}
                style={{ ...input, resize: 'vertical', minHeight: 150 }} />
              <div style={{ fontSize: 12, textAlign: 'right', marginTop: 4, color: tooLong ? '#b91c1c' : 'var(--text-tertiary, #64748b)' }}>
                {t('{n} / {max} words', { n: words, max: CONTACT_MAX_WORDS })}
              </div>
            </div>

            {error && <div style={{ fontSize: 13, color: '#b91c1c', margin: '8px 0' }}>{t(error)}</div>}

            <div style={{ display: 'flex', gap: 10, marginTop: 10 }}>
              <button type="button" className="btn btn-primary" onClick={submit}
                disabled={busy || !verified || !message.trim() || tooLong}>
                {busy && verified ? t('Sending…') : t('Submit')}
              </button>
              <button type="button" className="btn btn-secondary" onClick={onClose} disabled={busy}>{t('Cancel')}</button>
            </div>
          </>
        )}
      </div>
    </div>,
    document.body
  );
}

// Sidebar entry (patient + doctor dashboards) — sits just above Sign out.
export function ContactUsNavButton({ style }) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  return (
    <>
      <button type="button" onClick={(e) => { e.stopPropagation(); setOpen(true); }} className="nav-item"
        style={{ width: '100%', background: 'none', border: 'none', cursor: 'pointer', fontSize: 14, textAlign: 'left', ...style }}>
        <span>✉</span>{' '}{t('Contact Us')}
      </button>
      {open && <ContactUsModal mode="user" onClose={() => setOpen(false)} />}
    </>
  );
}
